import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {MatSidenav} from '@angular/material/sidenav';
import {Router} from '@angular/router';
import {isClickOutsideGivenElements} from '@application/helper/is-click-outside-given-elements';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {NavigationHistoryService} from '@application/navigation-history/navigation-history.service';
import {PropertyValue} from '@domain/property-value';
import {TechnicalDatasheet} from '@domain/technical-datasheet/technical-datasheet';
import {TuftTechnicalDatasheet} from '@domain/technical-datasheet/tuft-technical-datasheet';
import {AdvancedSearchInput} from '@presentation/components/search-filters/advanced-search/advanced-search-input.enum';
import {AssertionUtils, BaseComponent} from '@vdw/angular-component-library';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {TechnicalDatasheetThumbnailsComponent} from './technical-datasheet-thumbnails/technical-datasheet-thumbnails.component';

@Component({
  templateUrl: './technical-datasheet.component.html',
  styleUrls: ['./technical-datasheet.component.scss']
})
export class TechnicalDatasheetComponent extends BaseComponent implements OnInit {
  @ViewChild('technicalDatasheetThumbnailsComponent') public technicalDatasheetThumbnailsComponent: TechnicalDatasheetThumbnailsComponent;
  @ViewChild('sidenav') public sidenav: MatSidenav;

  public advancedSearchFilters: PropertyValue[] = [{propertyName: AdvancedSearchInput.NAME, propertyValue: ''}];
  public selectedTechnicalDatasheet: TechnicalDatasheet = null;

  private readonly listOfClassesThatShouldNotCloseSidebarOnClick = ['technical-datasheet-container', 'technical-datasheet', 'sidebar-small', 'cdk-overlay-container', 'toast-container'];
  private nameFilterChangeSubject: Subject<string> = new Subject<string>();

  public constructor(
    private readonly router: Router,
    private readonly navigationHistory: NavigationHistoryService
  ) {
    super();
  }

  @HostListener('document:click', ['$event'])
  public onOutsideDetailClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;

    if (this.sidenav.opened && isClickOutsideGivenElements(targetElement, this.listOfClassesThatShouldNotCloseSidebarOnClick)) {
      this.closeTechnicalSheetDetails();
    }
  }

  public ngOnInit(): void {
    this.subscribeToNameFilterChanges();
  }

  public nameFilterTextChanged(nameFilterText: string): void {
    this.nameFilterChangeSubject.next(nameFilterText);
  }

  public onSelectedTechnicalDatasheetChanged(technicalDatasheet: TechnicalDatasheet): void {
    this.selectedTechnicalDatasheet = technicalDatasheet;

    if (!AssertionUtils.isNullOrUndefined(this.selectedTechnicalDatasheet)) {
      this.navigationHistory.replaceState(RouteUtils.paths.texEdit.dataSheetDetails.absolutePath.replace(':id', `${this.selectedTechnicalDatasheet.id}`));
      this.sidenav.open();
    } else {
      this.closeTechnicalSheetDetails();
    }
  }

  public closeTechnicalSheetDetails(): void {
    this.sidenav.close();
    this.selectedTechnicalDatasheet = null;
    this.navigationHistory.replaceState(RouteUtils.paths.texEdit.dataSheets.absolutePath);
  }

  public onTechnicalDatasheetDeleted(deletedTechnicalDatasheet: TuftTechnicalDatasheet): void {
    this.closeTechnicalSheetDetails();
    this.technicalDatasheetThumbnailsComponent.listOfTechnicalDatasheets = this.technicalDatasheetThumbnailsComponent.listOfTechnicalDatasheets.filter(
      (technicalDatasheet: TechnicalDatasheet) => technicalDatasheet.id !== deletedTechnicalDatasheet.id
    );
  }

  private subscribeToNameFilterChanges(): void {
    this.nameFilterChangeSubject.pipe(debounceTime(500), takeUntil(this.unSubscribeOnViewDestroy)).subscribe((nameFilterText: string) => {
      this.closeTechnicalSheetDetails();
      this.advancedSearchFilters.find((value: PropertyValue) => value.propertyName === AdvancedSearchInput.NAME).propertyValue = nameFilterText;
    });
  }
}
